import React, { useState, useEffect } from "react";
import { X, Save, FileText, CheckCircle2, AlertCircle, RefreshCw, Gem, Plus, Minus } from "lucide-react";
import { pb } from "../../services/pocketbase";
import { produtoDadosService } from "../../services/produtoDadosService";
import { getBaseSku } from "../../utils/skuUtils";

// Lista padrão de zircônias e pedras populares para montagem rápida
const DEFAULT_POPULAR_STONES = [
  "Zircônia Branca 1.00mm",
  "Zircônia Branca 1.25mm",
  "Zircônia Branca 1.50mm",
  "Zircônia Branca 1.75mm",
  "Zircônia Branca 2.00mm",
  "Zircônia Branca 2.50mm",
  "Zircônia Branca 3.00mm",
  "Zircônia Branca 6.00mm",
  "Zircônia Água Marinha 6.00mm",
  "Zircônia Ametista 6.00mm",
  "Zircônia Esmeralda 6.00mm",
  "Zircônia Rubi 6.00mm",
  "Zircônia Safira 6.00mm",
  "Zircônia Turmalina Rosa 6.00mm",
  "Zircônia Turmalina Verde 6.00mm",
  "Zircônia Citrino 6.00mm",
  "Zircônia Black / Preta 1.25mm",
  "Zircônia Black / Preta 6.00mm"
];

/**
 * FichaTecnicaModal.js — Formulário de Edição da Ficha Técnica (Fase P3.1 + Stone Helper)
 *
 * Salva e atualiza o objeto `ficha_tecnica` na collection `produto_dados`, chaveado por `sku_pai`.
 * Inclui assistente visual para adição rápida de pedras/zircônias da base de dados.
 */
export default function FichaTecnicaModal({ isOpen, onClose, skuPai, initialFicha, onSuccess }) {
  const [formData, setFormData] = useState({
    peso_gramas: "",
    liga_metal: "Prata 925",
    detalhes_pedras: "",
    tempo_producao_dias: "3",
    estoque_minimo: "2",
    dimensoes: "",
    observacoes_tecnicas: ""
  });

  // Estados do Assistente de Pedras
  const [stoneSupplies, setStoneSupplies] = useState([]);
  const [loadingStones, setLoadingStones] = useState(false);
  const [pickerQty, setPickerQty] = useState(1);
  const [selectedStoneText, setSelectedStoneText] = useState(DEFAULT_POPULAR_STONES[0]);

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Carrega insumos do setor de pedras do banco
  useEffect(() => {
    if (!isOpen) return;

    let active = true;
    const fetchStoneSupplies = async () => {
      setLoadingStones(true);
      try {
        const records = await pb.collection("supplies").getFullList({
          filter: 'sector = "pedras" || category = "pedras" || name ~ "Zircônia" || name ~ "Pedra"',
          sort: "+name"
        });

        if (active && records && records.length > 0) {
          const names = records.map(r => r.name?.trim()).filter(Boolean);
          // Combina com pedras padrão evitando duplicatas
          const combined = Array.from(new Set([...names, ...DEFAULT_POPULAR_STONES])).sort();
          setStoneSupplies(combined);
          setSelectedStoneText(combined[0]);
        } else if (active) {
          setStoneSupplies(DEFAULT_POPULAR_STONES);
          setSelectedStoneText(DEFAULT_POPULAR_STONES[0]);
        }
      } catch (err) {
        console.warn("Erro ao buscar pedras na coleção supplies:", err);
        if (active) {
          setStoneSupplies(DEFAULT_POPULAR_STONES);
          setSelectedStoneText(DEFAULT_POPULAR_STONES[0]);
        }
      } finally {
        if (active) setLoadingStones(false);
      }
    };

    fetchStoneSupplies();
    return () => { active = false; };
  }, [isOpen]);

  // Preenche formulário ao abrir ou alterar ficha existente
  useEffect(() => {
    if (initialFicha && typeof initialFicha === "object") {
      let detalhesPedrasStr = "";
      if (Array.isArray(initialFicha.detalhes_pedras)) {
        detalhesPedrasStr = initialFicha.detalhes_pedras
          .map(p => `${p.qtd || 1}x ${p.tipo || "Pedra"}`)
          .join(" + ");
      } else {
        detalhesPedrasStr = initialFicha.detalhes_pedras || initialFicha.pedras || "";
      }

      setFormData({
        peso_gramas: initialFicha.peso_gramas || initialFicha.peso_bruto_g || initialFicha.peso || "",
        liga_metal: initialFicha.liga_metal || initialFicha.metal || "Prata 925",
        detalhes_pedras: detalhesPedrasStr,
        tempo_producao_dias: initialFicha.tempo_producao_dias || (initialFicha.tempo_producao_minutos ? String(Math.round(initialFicha.tempo_producao_minutos / 480)) : "") || initialFicha.tempo_producao || "3",
        estoque_minimo: initialFicha.estoque_minimo || "2",
        dimensoes: initialFicha.dimensoes || "",
        observacoes_tecnicas: initialFicha.observacoes_tecnicas || ""
      });
    } else {
      setFormData({
        peso_gramas: "",
        liga_metal: "Prata 925",
        detalhes_pedras: "",
        tempo_producao_dias: "3",
        estoque_minimo: "2",
        dimensoes: "",
        observacoes_tecnicas: ""
      });
    }
    setErrorMsg("");
  }, [isOpen, initialFicha]);

  if (!isOpen) return null;

  // Função para adicionar a pedra formatada no campo de texto
  const handleAddStoneToInput = () => {
    if (!selectedStoneText) return;

    const safeQty = Math.max(1, parseInt(pickerQty, 10) || 1);
    const formattedStone = `${safeQty}x ${selectedStoneText}`;
    
    setFormData(prev => {
      const current = prev.detalhes_pedras.trim();
      let updated = current;
      if (!current) {
        updated = formattedStone;
      } else {
        updated = `${current} + ${formattedStone}`;
      }
      return { ...prev, detalhes_pedras: updated };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!skuPai) return;

    const cleanPai = getBaseSku(skuPai);
    setSaving(true);
    setErrorMsg("");

    try {
      // Função para analisar a string de pedras e gerar o array estruturado
      const parseStonesString = (str) => {
        if (!str) return { stones: [], totalQty: 0 };
        const parts = str.split("+");
        const stones = [];
        let totalQty = 0;
        
        parts.forEach(part => {
          const clean = part.trim();
          if (!clean) return;
          
          // Procura por formatos como "12x Pedra", "12 x Pedra", "12 Pedra"
          const match = clean.match(/^(\d+)\s*[xX]?\s*(.+)$/);
          if (match) {
            const qty = parseInt(match[1], 10) || 1;
            const name = match[2].trim();
            const sizeMatch = name.match(/(\d+(?:\.\d+)?\s*mm)/i);
            const size = sizeMatch ? sizeMatch[1].trim() : "N/D";
            
            stones.push({
              qtd: qty,
              tipo: name,
              tamanho: size
            });
            totalQty += qty;
          } else {
            // Caso sem quantidade explícita, assume 1
            const sizeMatch = clean.match(/(\d+(?:\.\d+)?\s*mm)/i);
            const size = sizeMatch ? sizeMatch[1].trim() : "N/D";
            stones.push({
              qtd: 1,
              tipo: clean,
              tamanho: size
            });
            totalQty += 1;
          }
        });
        
        return { stones, totalQty };
      };

      const parsedStones = parseStonesString(formData.detalhes_pedras);
      const tempoDias = Number(formData.tempo_producao_dias) || 0;

      const fichaPayload = {
        peso_gramas: formData.peso_gramas.trim(),
        peso_bruto_g: formData.peso_gramas.trim(), // Suporta o frontend que lê peso_bruto_g
        liga_metal: formData.liga_metal.trim(),
        detalhes_pedras: parsedStones.stones, // Salva o array de objetos esperado
        qtd_pedras: parsedStones.totalQty, // Salva o total computado de pedras
        tempo_producao_dias: tempoDias,
        tempo_producao_minutos: tempoDias * 480, // Suporta o frontend que lê em minutos
        estoque_minimo: Number(formData.estoque_minimo) || 0,
        dimensoes: formData.dimensoes.trim(),
        observacoes_tecnicas: formData.observacoes_tecnicas.trim(),
        atualizado_em: new Date().toISOString()
      };

      await produtoDadosService.upsertProdutoDados(cleanPai, {
        ficha_tecnica: fichaPayload
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Erro ao salvar ficha técnica:", err);
      setErrorMsg(err.message || "Erro de conexão ao salvar a ficha técnica.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col">
        
        {/* HEADER DO MODAL */}
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-xl text-white">
              <FileText size={20} />
            </div>
            <div>
              <h2 className="text-base font-bold tracking-tight">Editar Ficha Técnica</h2>
              <p className="text-xs text-slate-400 font-mono">SKU PAI: {skuPai}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* FORMULÁRIO */}
        <form onSubmit={handleSave} className="p-6 space-y-4 text-xs">
          
          {errorMsg && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
              <AlertCircle size={16} className="shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {/* PESO EM GRAMAS */}
            <div>
              <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                Peso Estimado (g)
              </label>
              <input
                type="text"
                value={formData.peso_gramas}
                onChange={(e) => setFormData({ ...formData, peso_gramas: e.target.value })}
                placeholder="Ex: 4.5g"
                className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-semibold text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>

            {/* LIGA METÁLICA */}
            <div>
              <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                Liga Metálica / Metal
              </label>
              <select
                value={formData.liga_metal}
                onChange={(e) => setFormData({ ...formData, liga_metal: e.target.value })}
                className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-semibold text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value="Prata 925">Prata 925</option>
                <option value="Ouro 18K">Ouro 18K (750)</option>
                <option value="Ouro 10K">Ouro 10K (416)</option>
                <option value="Latão / Banhado">Latão / Banhado</option>
                <option value="Moeda Antiga">Moeda Antiga</option>
                <option value="Outro">Outro</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* TEMPO DE PRODUÇÃO (DIAS) */}
            <div>
              <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                Tempo de Produção (Dias Úteis)
              </label>
              <input
                type="number"
                min="0"
                value={formData.tempo_producao_dias}
                onChange={(e) => setFormData({ ...formData, tempo_producao_dias: e.target.value })}
                placeholder="Ex: 3"
                className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-semibold text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>

            {/* ESTOQUE MÍNIMO */}
            <div>
              <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                Estoque Mínimo Recomendado
              </label>
              <input
                type="number"
                min="0"
                value={formData.estoque_minimo}
                onChange={(e) => setFormData({ ...formData, estoque_minimo: e.target.value })}
                placeholder="Ex: 2"
                className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-semibold text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
          </div>

          {/* ASSISTENTE DE ADIÇÃO DE PEDRAS DA BASE */}
          <div className="bg-purple-50/60 border border-purple-200 rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between text-purple-900">
              <span className="font-bold flex items-center gap-1.5 text-[11px]">
                <Gem size={14} className="text-purple-600" />
                Assistente de Pedras / Zircônias (Base da Calculadora)
              </span>
              {loadingStones && <RefreshCw size={12} className="animate-spin text-purple-600" />}
            </div>

            <div className="flex items-center gap-2">
              <div className="shrink-0">
                <label className="text-[9px] font-bold text-purple-700 block uppercase mb-0.5">Qtd</label>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setPickerQty(prev => Math.max(1, (parseInt(prev, 10) || 1) - 1))}
                    className="w-7 h-7 rounded bg-purple-100 hover:bg-purple-200 text-purple-800 font-bold text-xs flex items-center justify-center cursor-pointer active:scale-95"
                    title="Diminuir quantidade"
                  >
                    <Minus size={11} />
                  </button>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={pickerQty !== undefined && pickerQty !== null ? pickerQty : ""}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setPickerQty(val);
                    }}
                    onBlur={() => {
                      if (!pickerQty || isNaN(parseInt(pickerQty, 10)) || parseInt(pickerQty, 10) < 1) {
                        setPickerQty(1);
                      } else {
                        setPickerQty(parseInt(pickerQty, 10));
                      }
                    }}
                    className="w-10 h-7 bg-white border border-purple-300 rounded text-center font-bold text-purple-900 text-xs outline-none focus:ring-1 focus:ring-purple-500"
                  />
                  <button
                    type="button"
                    onClick={() => setPickerQty(prev => (parseInt(prev, 10) || 0) + 1)}
                    className="w-7 h-7 rounded bg-purple-100 hover:bg-purple-200 text-purple-800 font-bold text-xs flex items-center justify-center cursor-pointer active:scale-95"
                    title="Aumentar quantidade"
                  >
                    <Plus size={11} />
                  </button>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <label className="text-[9px] font-bold text-purple-700 block uppercase">Selecione a Pedra da Base</label>
                <select
                  value={selectedStoneText}
                  onChange={(e) => setSelectedStoneText(e.target.value)}
                  className="w-full bg-white border border-purple-300 rounded-lg p-1.5 text-xs font-semibold text-purple-900 outline-none truncate"
                >
                  {stoneSupplies.map(stoneName => (
                    <option key={stoneName} value={stoneName}>{stoneName}</option>
                  ))}
                </select>
              </div>

              <div className="shrink-0 self-end">
                <button
                  type="button"
                  onClick={handleAddStoneToInput}
                  className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold text-xs shadow transition-all flex items-center gap-1"
                >
                  <Plus size={13} />
                  <span>Adicionar</span>
                </button>
              </div>
            </div>
          </div>

          {/* DETALHES DAS PEDRAS */}
          <div>
            <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
              Pedras / Zircônias Utilizadas (Texto Livre ou Adicionado Acima)
            </label>
            <input
              type="text"
              value={formData.detalhes_pedras}
              onChange={(e) => setFormData({ ...formData, detalhes_pedras: e.target.value })}
              placeholder="Ex: 1x Zircônia 6mm Branca + 12x 1.25mm Kraut"
              className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-semibold text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          {/* DIMENSÕES */}
          <div>
            <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
              Dimensões / Medidas
            </label>
            <input
              type="text"
              value={formData.dimensoes}
              onChange={(e) => setFormData({ ...formData, dimensoes: e.target.value })}
              placeholder="Ex: 15mm x 12mm x 4mm"
              className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-semibold text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>

          {/* OBSERVAÇÕES TÉCNICAS */}
          <div>
            <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
              Observações Técnicas de Fabricação
            </label>
            <textarea
              rows="3"
              value={formData.observacoes_tecnicas}
              onChange={(e) => setFormData({ ...formData, observacoes_tecnicas: e.target.value })}
              placeholder="Ex: Cuidados especiais no acabamento da cravação..."
              className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-medium text-slate-800 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none resize-none"
            />
          </div>

          {/* FOOTER */}
          <div className="pt-4 flex items-center justify-between border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:text-slate-800 font-semibold"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all shadow-md flex items-center gap-1.5 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <RefreshCw size={15} className="animate-spin" />
                  <span>Salvando...</span>
                </>
              ) : (
                <>
                  <Save size={15} />
                  <span>Salvar Ficha Técnica</span>
                </>
              )}
            </button>
          </div>

        </form>

      </div>
    </div>
  );
}
